import { useState, useEffect } from 'react';
import { useDatabase } from '@nozbe/watermelondb/react';
import { Q } from '@nozbe/watermelondb';
import { first, get, size } from 'global/lib/lodash';
import { guid, wait } from '../utils';
import { camelCase } from 'lodash';
import { awaitAll } from '../utils/awaitAll';
import { useSelector } from 'react-redux';

const none = null;

function objects_are_equal(x, y) {
  if (x === y) return true;
  // if both x and y are null or undefined and exactly the same

  if (!(x instanceof Object) || !(y instanceof Object)) return false;
  // if they are not strictly equal, they both need to be Objects

  if (x.constructor !== y.constructor) return false;
  // they must have the exact same prototype chain, the closest we can do is
  // test there constructor.

  for (var p in x) {
    if (!x.hasOwnProperty(p)) continue;
    // other properties were tested using x.constructor === y.constructor

    if (!y.hasOwnProperty(p)) return false;
    // allows to compare x[ p ] and y[ p ] when set to undefined

    if (x[p] === y[p]) continue;
    // if they have the same strict value or identity then they are equal

    if (typeof x[p] !== 'object') return false;
    // Numbers, Strings, Functions, Booleans must be strictly equal

    if (!objects_are_equal(x[p], y[p])) return false;
    // Objects and Arrays must be tested recursively
  }

  for (p in y) if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
  // allows x[ p ] to be set to undefined

  return true;
}

export const useDb = (type, _opts = {}) => {
  const db = useDatabase();
  const dbstate = useSelector(s => s.db);

  const [opts, setOpts] = useState(_opts);

  const id = opts.id || none;
  const where = opts.where || none;
  const loadRaw = get(opts, 'loadRaw', true);

  const [state, setState] = useState({
    data: [],
    loading: true,
  });

  useEffect(() => {
    if (!objects_are_equal(opts, _opts)) {
      setOpts(_opts);
    }
  }, [_opts]);

  const getData = async () => {
    setState((s) => ({ ...s, loading: true }));

    if (dbstate.syncing) {
      return;
    }

    await wait(50);

    let items = [];
    if (id) {
      items = await db.get(type).query(Q.where('id', id));
    } else if (where) {
      const keys = Object.keys(where).map((k) => where[k]);
      const validKeys = keys.filter((f) => !!f);

      if (size(validKeys) < size(keys)) {
        return;
      }

      const q = Object.keys(where).map((k) => {
        return Q.where(k, where[k]);
      });

      items = await db.get(type).query(...q);
    } else {
      items = await db.get(type).query();
    }

    items = await awaitAll(
      items.map((item) => () => async () => {
        (await item.load) && item.load(opts.include);

        if (loadRaw && item.toRaw) {
          item = item.toRaw();
        }

        return item;
      })
    , { queueSize: 5 });

    setState((s) => ({ ...s, data: id ? first(items) : items, loading: false }));
  };

  const saveData = async (d) => {
    let result;

    await db.write(async () => {
      console.log(d);

      const itemid = get(state, 'data.id');

      const attr = get(d, 'data.attributes', {});

      const item = state.data;
      let newitem = {}

      //loop attrs and update new item, especially with newly created media
      await Promise.all(Object.keys(attr).map(async (k) => {
        let val = attr[k];
        
        if (k === 'images') {
          val = await Promise.all(val.map(async img => {
            if (img.file) {
              const newImage = await db.get('media').create(ni => {
                ni._raw.id = guid();
                ni.fullUrl = img.data;
                ni.name = get(img, 'file.name');
                ni.fileName = get(img, 'file.name');
                ni.mimeType = get(img, 'file.type');
                ni.size = get(img, 'file.size');
                ni.disk = 's3';
                ni.type = 'image';
                ni.conversionsDisk = 's3';
                ni.modelId = itemid;
                ni.modelType = type;
                ni.collectionName = `${type}-images`;
                ni.isNew = 1;
              })

              // const r = newImage.toRaw();

              return newImage;
            } else {
              return img;
            }
          }))
        }

        newitem[camelCase(k)] = val;
      }))

      if (itemid) {
        await item.update(it => {
          for (let k in newitem) {
            it[k] = newitem[k];
          }

          result = it;
        });
      } else {
        await db.get(type).create(it => {
          for (let k in newitem) {
            it[k] = newitem[k];
          }

          result = it;
        })
      }
    });

    return result;
  };

  useEffect(() => {
    getData();
  }, [type, id, where, dbstate.syncing]);

  return {
    ...state,

    update: getData,
    saveData,

    db,
  };
};
